import React from 'react';
import clsx from 'clsx';
import styles from './Skeleton.module.css';

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  borderRadius?: string;
  className?: string;
  variant?: 'text' | 'rectangular' | 'circular';
  count?: number;
}

const Skeleton: React.FC<SkeletonProps> = ({
  width,
  height,
  borderRadius,
  className,
  variant = 'rectangular',
  count = 1,
}) => {
  const style: React.CSSProperties = {
    width,
    height,
    borderRadius,
  };

  if (count > 1) {
    return (
      <>
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className={clsx(styles.skeleton, styles[variant], className)}
            style={style}
          />
        ))}
      </>
    );
  }

  return (
    <div
      className={clsx(styles.skeleton, styles[variant], className)}
      style={style}
    />
  );
};

/* ==========================================
   ProductCard Skeleton
   ========================================== */

interface ProductCardSkeletonProps {
  compact?: boolean;
}

const ProductCardSkeleton: React.FC<ProductCardSkeletonProps> = ({ compact }) => (
  <div className={clsx(styles.productCard, compact && styles.productCardCompact)}>
    <Skeleton className={styles.productCardImage} />
    <div className={styles.productCardContent}>
      <Skeleton variant="text" width="40%" height={12} />
      <Skeleton variant="text" width="80%" height={16} />
      <Skeleton variant="text" width="60%" height={12} />
      <Skeleton variant="text" width="30%" height={18} />
    </div>
  </div>
);

/* ==========================================
   CategoryCard Skeleton
   ========================================== */

const CategoryCardSkeleton: React.FC = () => (
  <div className={styles.categoryCard}>
    <Skeleton className={styles.categoryCardImage} />
    <div className={styles.categoryCardContent}>
      <Skeleton variant="text" width="70%" height={16} />
      <Skeleton variant="text" width="90%" height={12} />
      <Skeleton variant="text" width="40%" height={12} />
    </div>
  </div>
);

/* ==========================================
   BrandCard Skeleton
   ========================================== */

const BrandCardSkeleton: React.FC = () => (
  <div className={styles.brandCard}>
    <Skeleton variant="circular" width={80} height={80} />
    <Skeleton variant="text" width="60%" height={16} />
    <Skeleton variant="text" width="80%" height={12} />
    <Skeleton variant="text" width="90%" height={12} />
  </div>
);

/* ==========================================
   ProductDetail Skeleton
   ========================================== */

const ProductDetailSkeleton: React.FC = () => (
  <div className={styles.productDetail}>
    <div className={styles.productDetailBreadcrumb}>
      <Skeleton variant="text" width={60} height={14} />
      <Skeleton variant="text" width={80} height={14} />
      <Skeleton variant="text" width={120} height={14} />
    </div>
    <div className={styles.productDetailGrid}>
      <div>
        <Skeleton className={styles.productDetailMainImage} />
        <div className={styles.productDetailThumbnails}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} width={72} height={72} borderRadius="var(--radius-md)" />
          ))}
        </div>
      </div>
      <div className={styles.productDetailInfo}>
        <Skeleton variant="text" width="30%" height={14} />
        <Skeleton variant="text" width="90%" height={28} />
        <Skeleton variant="text" width="40%" height={24} />
        <Skeleton variant="text" width="100%" height={14} count={3} />
        <div className={styles.productDetailActions}>
          <Skeleton width={140} height={48} borderRadius="var(--radius-lg)" />
          <Skeleton width={180} height={48} borderRadius="var(--radius-lg)" />
        </div>
        <div className={styles.productDetailSpecs}>
          <Skeleton variant="text" width="30%" height={18} />
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="text" width="70%" height={14} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* ==========================================
   AdminTable Skeleton
   ========================================== */

interface AdminTableSkeletonProps {
  rows?: number;
  columns?: number;
}

const AdminTableSkeleton: React.FC<AdminTableSkeletonProps> = ({
  rows = 6,
  columns = 5,
}) => (
  <div className={styles.adminTable}>
    <Skeleton className={styles.adminTableHeader} />
    {Array.from({ length: rows }).map((_, rowIdx) => (
      <div key={rowIdx} className={styles.adminTableRow}>
        {Array.from({ length: columns }).map((_, colIdx) => (
          <Skeleton
            key={colIdx}
            variant="text"
            width={`${Math.max(10, Math.random() * 20 + 10)}%`}
            height={16}
          />
        ))}
      </div>
    ))}
  </div>
);

/* ==========================================
   Order Card Skeleton
   ========================================== */

const OrderCardSkeleton: React.FC = () => (
  <div className={styles.orderCard}>
    <div className={styles.orderCardHeader}>
      <div>
        <Skeleton variant="text" width={140} height={16} />
        <Skeleton variant="text" width={100} height={12} />
      </div>
      <Skeleton width={80} height={28} borderRadius="var(--radius-full)" />
    </div>
    <div className={styles.orderCardItems}>
      <Skeleton width={60} height={60} borderRadius="var(--radius-md)" />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Skeleton variant="text" width="60%" height={14} />
        <Skeleton variant="text" width="30%" height={12} />
      </div>
      <Skeleton variant="text" width={80} height={16} />
    </div>
  </div>
);

/* ==========================================
   Order Detail Skeleton
   ========================================== */

const OrderDetailSkeleton: React.FC = () => (
  <div style={{ maxWidth: 900, margin: '0 auto', padding: 'var(--spacing-lg) var(--spacing-md)' }}>
    <Skeleton variant="text" width={200} height={28} />
    <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className={styles.orderCard}>
        <Skeleton variant="text" width="50%" height={18} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 8 }}>
          <Skeleton variant="text" height={14} count={3} />
          <Skeleton variant="text" height={14} count={3} />
        </div>
      </div>
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className={styles.orderCardItems}>
          <Skeleton width={80} height={80} borderRadius="var(--radius-md)" />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Skeleton variant="text" width="70%" height={16} />
            <Skeleton variant="text" width="40%" height={14} />
          </div>
          <Skeleton variant="text" width={80} height={16} />
        </div>
      ))}
    </div>
  </div>
);

/* ==========================================
   Profile Skeleton
   ========================================== */

const ProfileSkeleton: React.FC = () => (
  <div className={styles.profileSkeleton}>
    <div className={styles.profileHeader}>
      <Skeleton variant="circular" width={80} height={80} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Skeleton variant="text" width="40%" height={24} />
        <Skeleton variant="text" width="30%" height={14} />
      </div>
    </div>
    <div className={styles.profileFields}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className={styles.profileField}>
          <Skeleton variant="text" width={100} height={12} />
          <Skeleton height={40} borderRadius="var(--radius-md)" />
        </div>
      ))}
    </div>
  </div>
);

/* ==========================================
   PageLoader Skeleton
   ========================================== */

const PageLoaderSkeleton: React.FC = () => (
  <div className={styles.pageLoader}>
    <div className={styles.pageLoaderContent}>
      <Skeleton variant="text" width="60%" height={24} />
      <Skeleton variant="text" width="100%" height={14} count={3} />
      <Skeleton width="100%" height={200} borderRadius="var(--radius-lg)" />
    </div>
  </div>
);

/* ==========================================
   Settings Skeleton
   ========================================== */

const SettingsSkeleton: React.FC = () => (
  <div className={styles.settingsSkeleton}>
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className={styles.settingsCard}>
        <Skeleton variant="text" width="30%" height={20} />
        <div className={styles.settingsRow}>
          {Array.from({ length: 2 }).map((_, j) => (
            <div key={j} className={styles.profileField}>
              <Skeleton variant="text" width={120} height={12} />
              <Skeleton height={40} borderRadius="var(--radius-md)" />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

/* ==========================================
   Dashboard Stat Card Skeleton
   ========================================== */

const StatCardSkeleton: React.FC = () => (
  <div className={styles.statCard}>
    <Skeleton variant="text" width="50%" height={14} />
    <Skeleton variant="text" width="40%" height={28} />
    <Skeleton variant="text" width="60%" height={12} />
  </div>
);

export default Skeleton;
export {
  Skeleton,
  ProductCardSkeleton,
  CategoryCardSkeleton,
  BrandCardSkeleton,
  ProductDetailSkeleton,
  AdminTableSkeleton,
  OrderCardSkeleton,
  OrderDetailSkeleton,
  ProfileSkeleton,
  PageLoaderSkeleton,
  SettingsSkeleton,
  StatCardSkeleton,
};
